import { createSignal, onCleanup } from "solid-js";

/**
 * Reactive media query hook for SolidJS.
 * Returns a signal that updates when the media query match state changes.
 *
 * @param query - A CSS media query string, e.g. "(max-width: 40rem)"
 * @returns An accessor that returns true when the query matches.
 *
 * @example
 * const isMobile = useMediaQuery("(max-width: 40rem)");
 * <Show when={isMobile()} fallback={<DesktopView />}>
 *   <MobileView />
 * </Show>
 */
export function useMediaQuery(query: string) {
	const mql = window.matchMedia(query);
	const [matches, setMatches] = createSignal(mql.matches);
	const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
	mql.addEventListener("change", handler);
	onCleanup(() => mql.removeEventListener("change", handler));

	return matches;
}

/** Matches the mobile breakpoint used across the codebase (≤ 40rem). */
export function useIsMobile() {
	return useMediaQuery("(max-width: 40rem)");
}

/**
 * Some panels, such as the global settings panel, should be larger than the default panel width,
 * because they contain a lot of content.
 * This function returns the width of the panel, based on the body width.
 * @returns The width of the panel, in pixels.
 */
export const getPanelWidth = () => {
	if (window.matchMedia("(width < 40rem)").matches) {
		return "90%";
	}
	const width = document.body.clientWidth;
	const max = Math.min(width - 50, 720);
	return `${max}px`;
};
